import { EmptyState, Skeleton } from "@design";
import { useQuery } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";
import { type DuplicatePair, getSpaceOptions } from "@/api";
import { Page } from "@/components/Page";
import { PageBreadcrumbs } from "@/components/PageBreadcrumbs";
import { Text } from "@/components/Text";
import { spaceCrumb } from "@/features/spaces/spaceCrumb";
import { DuplicatePairCard } from "./DuplicatePairCard";
import { MergeHistory } from "./MergeHistory";
import { MergeModal } from "./MergeModal";
import { useDismissDuplicate, useDuplicates, useMerges } from "./useDuplicates";

// Order-independent: the same two pages may come back the other way round. A plain code-unit
// comparison on purpose: this is an identity key, so it must not depend on the user's locale.
const pairKey = (p: DuplicatePair) => (p.a.path < p.b.path ? `${p.a.path}|${p.b.path}` : `${p.b.path}|${p.a.path}`);

/**
 * A space's possible duplicate pages (docs/design/knowledge-graph/KNOWLEDGE_GRAPH_DESIGN.md §6,
 * entity resolution), for editors: compare side by side, then Merge, Not the
 * same (never suggested again) or Skip (for now). Recent merges can be undone.
 */
export function DuplicatesPage() {
  const { t } = useTranslation("knowledge");
  const { spaceUuid = "" } = useParams();
  const space = useQuery({ ...getSpaceOptions({ path: { space_uuid: spaceUuid } }), select: (r) => r.data });
  const pairs = useDuplicates(spaceUuid);
  const merges = useMerges(spaceUuid);
  const dismiss = useDismissDuplicate(spaceUuid);
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [merging, setMerging] = useState<DuplicatePair | null>(null);

  const shown = (pairs.data ?? []).filter((p) => !skipped.has(pairKey(p)));
  let body: ReactNode;
  if (pairs.isPending) {
    body = <Skeleton className="h-48 w-full" />;
  } else if (pairs.isError) {
    body = (
      <Text role="alert" tone="error">
        {t("duplicates.loadFailed")}
      </Text>
    );
  } else if (shown.length === 0) {
    body = <EmptyState icon={<Copy />} title={t("duplicates.emptyTitle")} body={t("duplicates.emptyBody")} />;
  } else {
    body = (
      <section aria-label={t("duplicates.listLabel")} className="flex flex-col gap-3">
        {shown.map((pair) => (
          <DuplicatePairCard
            key={pairKey(pair)}
            pair={pair}
            spaceUuid={spaceUuid}
            onMerge={() => setMerging(pair)}
            onDismiss={() =>
              dismiss.mutate({ path: { space_uuid: spaceUuid }, body: { pathA: pair.a.path, pathB: pair.b.path } })
            }
            onSkip={() => setSkipped((s) => new Set(s).add(pairKey(pair)))}
            dismissing={
              dismiss.isPending &&
              dismiss.variables?.body.pathA === pair.a.path &&
              dismiss.variables?.body.pathB === pair.b.path
            }
          />
        ))}
      </section>
    );
  }

  return (
    <>
      <PageBreadcrumbs
        trail={[spaceCrumb(spaceUuid, space.data?.name), { label: t("title"), to: `/spaces/${spaceUuid}/knowledge` }]}
        current={t("duplicates.title")}
      />
      <Page
        title={t("duplicates.title")}
        subtitle={space.data ? t("duplicates.subtitle", { space: space.data.name }) : undefined}
      >
        {body}
        <MergeHistory spaceUuid={spaceUuid} merges={merges.data ?? []} />
        {merging && <MergeModal pair={merging} spaceUuid={spaceUuid} onClose={() => setMerging(null)} />}
      </Page>
    </>
  );
}
